<script setup>
import { ref, computed } from 'vue'
import HeartIcon from './fragments/HeartIcon.vue'
import StarIcon from './fragments/StarIcon.vue'
import RadioIcon from './fragments/RadioIcon.vue'
import { TYPE_OPTIONS } from './consts'

const props = defineProps({
  size: {
    type: Number,
    default: 24
  },
  disabled: {
    type: Boolean,
    default: false
  },
  hovered: {
    type: Boolean,
    default: false
  },
  checked: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'radio',
    validator: val => TYPE_OPTIONS.includes(val)
  }
})

const emit = defineEmits([ 'update:checked' ])
const clicked = ref(false)

const toggle = () => {
  if (props.disabled) return

  const isChecked = !props.checked

  if (isChecked) {
    clicked.value = true
    setTimeout(() => (clicked.value = false), 350)
  }

  emit('update:checked', isChecked)
}

const internalHover = ref(false)

const isHovered = computed(() => {
  if (props.hovered !== false) return props.hovered
  return internalHover.value
})

const cssVars = computed(() => {
  if (props.disabled) {
    return {
      '--icon-toggle-fill': 'var(--icon-toggle-disabled-unchecked-fill)',
      '--icon-toggle-outline': 'var(--icon-toggle-disabled-outline)',
      '--icon-toggle-glow-color': 'var(--icon-toggle-disabled-glow-color)'
    }
  }

  return {
    '--icon-toggle-fill': `var(--icon-toggle-${props.type}-fill)`,
    '--icon-toggle-outline': `var(--icon-toggle-${props.type}-outline)`,
    '--icon-toggle-glow-color': `var(--icon-toggle-${props.type}-glow-color)`
  }
})

const iconMap = {
  heart: HeartIcon,
  star: StarIcon,
  radio: RadioIcon
}
</script>

<template>
  <button
    type="button"
    class="c-icon-button"
    :class="{ checked, clicked, disabled, hovered: isHovered }"
    :style="{ ...cssVars, width: size + 'px', height: size + 'px' }"
    :aria-disabled="disabled ? 'true' : 'false'"
    :disabled="disabled"
    @mouseenter="internalHover = true"
    @mouseleave="internalHover = false"
    @click="toggle"
  >
    <component
      :is="iconMap[props.type]"
      v-bind="{ ...props }"
    />
  </button>
</template>

<style lang="scss">
:root {
   /* Heart */
  --icon-toggle-heart-fill: rgba(255, 75, 140, 1);
  --icon-toggle-heart-glow-color: 255, 75, 140;

  /* Star */
  --icon-toggle-star-fill: rgba(255, 194, 75, 1);
  --icon-toggle-star-glow-color: 255, 194, 75;

  /* Radio */
  --icon-toggle-radio-fill: rgba(18, 30, 72, 0.8);
  --icon-toggle-radio-glow-color: rgba(18, 30, 72, 1);

  /* Disabled */
  --icon-toggle-disabled-checked-fill: rgba(18, 30, 72, 0.15);
  --icon-toggle-disabled-unchecked-fill: rgba(18, 30, 72, 0.05);
  --icon-toggle-disabled-outline: rgba(18, 30, 72, 0.10);

  /* Defaults (can be overridden) */
  --icon-toggle-fill: var(--icon-toggle-radio-fill);
  --icon-toggle-outline: var(--icon-toggle-disabled-outline);
  --icon-toggle-glow-color: var(--icon-toggle-radio-glow-color);
}

.c-icon-button {
  all: unset;
  display: inline-grid;
  place-items: center;
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  user-select: none;
  transition: transform 150ms ease, box-shadow 160ms ease;
  background: transparent;

  &.clicked {
    filter: drop-shadow(0 0 1px rgba(var(--icon-toggle-glow-color), 1));

    .icon-toggle-halo-1,
    .icon-toggle-halo-2,
    .icon-toggle-halo-3 {
      animation: toggle-ripple 0.35s ease-out forwards;
    }
  }

  &.hovered:not(.disabled):not(.checked) {
    .icon-toggle-shape:not(.icon-toggle-radio) {
      fill: none;
      stroke: var(--icon-toggle-fill);
      filter: drop-shadow(0 0 1px rgba(var(--icon-toggle-glow-color), 1));
    }

    .icon-toggle-shape.icon-toggle-radio {
      fill: none;
      stroke: none;
    }

    .icon-toggle-outline {
      stroke: var(--icon-toggle-fill);
      fill: none;
      opacity: 1;
      filter: drop-shadow(0 0 1px var(--icon-toggle-glow-color));
    }
  }

  &.hovered:not(.disabled).checked {
    .icon-toggle-shape:not(.icon-toggle-radio) {
      opacity: 0.7;
      filter: drop-shadow(0 10px 8px rgba(var(--icon-toggle-glow-color), 0.6));
    }

    .icon-toggle-shape.icon-toggle-radio {
      opacity: 1;
    }

    .icon-toggle-outline {
      opacity: 1;
      filter: drop-shadow(0 0 1px var(--icon-toggle-glow-color));
    }
  }

  &.checked {
    .icon-toggle-shape:not(.icon-toggle-radio) {
      fill: var(--icon-toggle-fill);
      stroke: var(--icon-toggle-fill);
      filter: drop-shadow(0 8px 8px rgba(var(--icon-toggle-glow-color), 0.3));
    }

    .icon-toggle-shape.icon-toggle-radio {
      fill: var(--icon-toggle-fill);
    }

    &:not(.disabled) {
      .icon-toggle-outline {
        stroke: var(--icon-toggle-fill);
        opacity: 0.1;
      }
    }

    &:not(.disabled):hover {
      .icon-toggle-shape:not(.icon-toggle-radio) {
        opacity: 0.7;
        filter: drop-shadow(0 10px 8px rgba(var(--icon-toggle-glow-color), 0.6));
      }

      .icon-toggle-shape.icon-toggle-radio {
        opacity: 1;
      }

      .icon-toggle-outline {
        opacity: 1;
        filter: drop-shadow(0 0 1px var(--icon-toggle-glow-color));
      }
    }
  }

  &.disabled {
    cursor: not-allowed;

    .icon-toggle-outline {
      fill: var(--icon-toggle-disabled-unchecked-fill);
      stroke: var(--icon-toggle-disabled-outline);
    }

    &.checked {
      .icon-toggle-shape:not(.icon-toggle-radio) {
        fill: var(--icon-toggle-disabled-checked-fill);
        stroke: none;
      }

      .icon-toggle-shape.icon-toggle-radio {
        fill: rgba(18, 30, 72, 0.3);
      }
    }

    &:not(.checked) {
      .icon-toggle-shape:not(.icon-toggle-radio) {
        fill: var(--icon-toggle-disabled-unchecked-fill);
        stroke: var(--icon-toggle-disabled-outline);
      }

      .icon-toggle-shape.icon-toggle-radio {
        fill: none;
        stroke: none;
      }
    }
  }
}

.icon-toggle {
  transform-origin: 50% 50%;
  transition: filter 0.2s ease-out;
  overflow: visible;
}

.icon-toggle-group {
  transform: scale(2.5) translate(-19px, -16.5px);
}

.icon-toggle-shape:not(.icon-toggle-radio) {
  fill: none;
  stroke: var(--icon-toggle-disabled-outline);
  stroke-width: 1.2px;
  transition: all 180ms ease;
  transform-origin: 50% 50%;
}

.icon-toggle-shape.icon-toggle-radio {
  fill: none;
  stroke: none;
}

.icon-toggle-outline {
  fill: none;
  stroke: var(--icon-toggle-disabled-outline);
  transform-origin: 50% 50%;
  transition: all 180ms ease;
}

.icon-toggle-halo {
  fill: none;
  stroke: var(--icon-toggle-fill);
  opacity: 0;
  transform-origin: 50% 50%;
  transition: none;
}

@keyframes toggle-ripple {
  0%   { opacity: 1; }
  30%  { opacity: 0.8; }
  50%  { opacity: 0.5; }
  80%  { opacity: 0.3; }
  100% { opacity: 0; }
}
</style>
